import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MailCheck, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function VerifyEmail() {
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/verify-email", { code });
      await refresh();
      toast.success("Email verified — welcome aboard!");
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const resend = async () => {
    setErr("");
    try {
      const { data } = await api.post("/auth/resend-verification");
      toast.success(data?.message || "A new code is on its way.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const signOut = async () => { await logout(); nav("/login", { replace: true }); };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12" data-testid="verify-email-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8">
        <div className="flex items-center gap-2 mb-2">
          <MailCheck className="w-5 h-5 text-blue-600" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Verify your email</span>
        </div>
        <h1 className="text-3xl font-display font-bold text-slate-900 tracking-tight">One quick step</h1>
        <p className="mt-1 text-sm text-slate-600">
          We emailed a 6-digit code to <span className="font-semibold">{user?.email}</span>.
          Enter it to unlock your account and the labs.
        </p>
        {err && <Alert variant="destructive" className="mt-4" data-testid="verify-page-error"><AlertDescription>{err}</AlertDescription></Alert>}
        <form onSubmit={verify} className="mt-6 space-y-3">
          <div>
            <Label htmlFor="vcode">Verification code</Label>
            <Input id="vcode" data-testid="verify-page-code-input" inputMode="numeric" maxLength={6} required value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="font-mono text-lg tracking-widest" placeholder="------" />
          </div>
          <Button data-testid="verify-page-submit-button" type="submit" className="w-full" disabled={loading || code.length < 6}>
            {loading ? "Verifying…" : "Verify & continue"}
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={resend} data-testid="verify-page-resend-button">
            Didn't get it? Resend code
          </Button>
        </form>
        <div className="mt-6 pt-4 border-t border-slate-200">
          <button onClick={signOut} data-testid="verify-page-signout" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
            <LogOut className="w-3.5 h-3.5" /> Sign in with a different account
          </button>
        </div>
      </div>
    </div>
  );
}
